import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import PersonnelToast from '../../components/PersonnelToast';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import BenefitRate from 'src/models/personnel/BenefitRate';

interface IEditBenefitRateDialogProps {
    dialogName: string;
    hideDialog: () => void;
    updateBenefit: () => void;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
    benefitRate: BenefitRate;
}

const EditBenefitRateDialog = (props: IEditBenefitRateDialogProps) => {
    const { register, errors, setValue, getValues } = useForm();
    const { put, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);

    useEffect(() => {
        onCreate();
    }, []);

    // ensures all values are set and formatted
    useEffect(() => {
        if (props.benefitRate) {
            setValue('category', props?.benefitRate?.category);
            setValue('description', props?.benefitRate?.description);
            setValue('totalMonthAmount', props?.benefitRate?.totalMonthAmount?.toFixed(2));
            setValue('companyMonthAmount', props?.benefitRate?.companyMonthAmount?.toFixed(2));
            setValue('companyAnnualAmount', props?.benefitRate?.companyAnnualAmount?.toFixed(2));
            setValue('totalAnnualAmount', props?.benefitRate?.totalAnnualAmount?.toFixed(2));
            setValue('compensationType', props?.benefitRate?.compensationType);
        }
    }, [props.benefitRate]);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const onCreate = () => {
        console.log('dialog hooked up: edit benefit');
    };

    const handleSubmit = () => {
        let formValues = getValues();
        let newData: BenefitRate = { ...props.benefitRate };

        newData.compensationType = formValues.compensationType;
        newData.totalMonthAmount = parseFloat(formValues.totalMonthAmount);
        newData.companyMonthAmount = parseFloat(formValues.companyMonthAmount);

        newData.totalAnnualAmount = newData.totalMonthAmount * 12;

        if (newData.compensationType === 'A') {
            newData.companyAnnualAmount = newData.companyMonthAmount * 12;
        } else {
            newData.companyAnnualAmount = newData.companyMonthAmount;
        }

        console.log(newData);

        updateBenefit(newData);
    };

    const updateBenefit = async (data: BenefitRate) => {
        await put(`/v1.0/landmen/benefits/update-yearly-rate`, data);

        if (response.ok) {
            props.showToast('Successfully updated benefit');
            props.updateBenefit();
            props.hideDialog();
        } else {
            showToast('Unable to update benefit: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    const updateTotalAnnualAmmount = () => {
        let values = getValues();
        setValue('totalAnnualAmount', (values.totalMonthAmount * 12).toFixed(2));
    };

    const updateTotalCompanyAnnualAmount = () => {
        let values = getValues();
        if (values.compensationType === 'P') {
            setValue('companyAnnualAmount', parseFloat(values.companyMonthAmount)?.toFixed(2));
        } else {
            setValue('companyAnnualAmount', (values.companyMonthAmount * 12)?.toFixed(2));
        }
    };

    return (
        <Dialog title="Edit Yearly Rate" dialogName={props.dialogName} bodyStyle={{ width: '5%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <div>
                <form onSubmit={handleSubmit}>
                    <RHFInputBox
                        name={'category'}
                        label={'Category'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        disabled={true}
                        isRequired={true}
                        spaceLabelContent={true}
                    />
                    <RHFInputBox
                        name={'description'}
                        label={'Description'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        disabled={true}
                        isRequired={true}
                        spaceLabelContent={true}
                    />
                    <RHFInputBox
                        name={'totalMonthAmount'}
                        label={'Total Month Amount'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        isRequired={true}
                        spaceLabelContent={true}
                        onBlur={() => updateTotalAnnualAmmount()}
                    />
                    <RHFInputBox
                        name={'companyMonthAmount'}
                        label={'Company Month Amount'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        isRequired={true}
                        spaceLabelContent={true}
                        onBlur={() => updateTotalCompanyAnnualAmount()}
                    />
                    <RHFInputBox
                        name={'totalAnnualAmount'}
                        label={'Total Annual Amount'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        disabled={true}
                        isRequired={true}
                        spaceLabelContent={true}
                    />
                    <RHFInputBox
                        name={'companyAnnualAmount'}
                        label={'Company Annual Amount'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        disabled={true}
                        isRequired={true}
                        spaceLabelContent={true}
                    />
                    <RHFSelect
                        name={'compensationType'}
                        label={'Compensation Type'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        isRequired={true}
                        options={[
                            { value: 'P', label: 'Percentage' },
                            { value: 'A', label: 'Amount' },
                        ]}
                        spaceLabelContent={true}
                        onChange={() => updateTotalCompanyAnnualAmount()}
                    />
                </form>
            </div>
            <div className="button-row" style={{ paddingBottom: '1rem' }}>
                <Button text="Save" onClick={handleSubmit} intent="primary" />
                <Button text="Cancel" onClick={() => props.hideDialog()} intent="secondary" />
            </div>
        </Dialog>
    );
};

export default EditBenefitRateDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/benefits/EditBenefitRateDialog.tsx